/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */
import { kinetikLightTheme, KinetikThemeProvider } from '@kinetik/ui-kit';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { push } from 'connected-react-router';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { Settings } from 'luxon';
import React, { memo, Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntercom } from 'react-use-intercom';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import { AppErrorBoundary } from '@/components/common';
import {
  TRIP_COUNTS_POLL_INTERVAL_MS,
  TRIP_STATUS_CODES,
  TRIP_TYPE_CODES,
} from '@/constants';
import {
  useExternalTripRequestsFlag,
  useIsCorrectionTableEnabled,
} from '@/hooks';
import { lazyLoad } from '@/lib/utils';
import { AppRoutes } from '@/routes';
import {
  clearIntercomJwt,
  getIntercomTokenForCurrentUser,
  initPendo,
} from '@/utils';
import ActionAlerts from 'components/ActionAlerts/Loadable';
import Alerts from 'components/Alerts/Loadable';
import InactivityModal from 'components/InactivityModal/Loadable';
import LoadingSpinner from 'components/LoadingSpinner/Loadable';
import { TableProvider } from 'components/v2/Table';
import ThemeProviderWrapper from 'containers/ThemeProviderWrapper';
import GlobalStyle from 'global-styles';
import { newLayout } from 'theme';
import accessTokenService, { ACCESS_TOKEN_KEY } from 'utils/accessTokenService';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectSaga } from 'utils/injectSaga';

import {
  attemptLogout,
  changeTheme,
  getNeedsActionTripsCount as getNeedsActionTripsCountAction,
  getTripGroupings,
  getTripsCount,
  ldContextIdentified,
  loadOrganizations,
  loadRoles,
  logoutSuccess,
  removeActionAlert,
  removeAppNotification,
  removeOldestMessage,
  removeOneMessage,
  resetGlobalSearchResults,
  setActiveMenu,
  setActiveOrganization,
  setActiveTab,
  setExpanded,
  setInactivityModalVisibility,
} from './actions';
import { UI_TYPES } from './constants';
import { useRefreshRolesOnNavigation } from './hooks';
import reducer from './reducer';
import saga from './saga';
import { selectAppShellState } from './selectors';
import { newLayoutTheme } from './themes';

const ToastNotifications = lazyLoad(
  () => import('@/features/toast-notifications/components/ToastNotifications'),
);

export const App = () => {
  useInjectReducer({ key: 'app', reducer });
  useInjectSaga({ key: 'appSaga', saga });

  const dispatch = useDispatch();

  const appState = useSelector(selectAppShellState);

  const {
    user,
    currentUserRole,
    organizations,
    inactivityModalVisibility,
    activeOrganization,
    messages,
    redirectUrl,
    redirectPageName,
    alertMessages,
    appNotifications,
    loading,
    isLoadingUser,
    isLoadingOrganizations,
    isLoadingAccessToken,
    activeTab,
    activeMenu,
    expanded,
    totalUnreadMessageCount,
  } = appState;

  // LaunchDarkly config
  const ldClient = useLDClient();
  const isExternalTripRequestsEnabled = useExternalTripRequestsFlag();
  const isCorrectionTableEnabled = useIsCorrectionTableEnabled();

  // Intercom config
  const [intercomInitiated, setIntercomInitiated] = useState(false);
  const {
    boot: intercomBoot,
    hardShutdown: intercomHardShutdown,
    update: intercomUpdate,
  } = useIntercom();

  /**
   * Initializes the Intercom chat widget with user and organization data
   *
   * This function configures and boots the Intercom widget when the intercom feature
   * flag is enabled. It passes user details, organization information, and custom
   * attributes to personalize the Intercom experience.
   *
   * @param {Object} params - Parameters object containing feature flags
   * @param {boolean} params.intercom - Flag indicating if Intercom should be enabled
   * @returns {void}
   */
  const initIntercom = useCallback(
    async ({ intercom: intercomFeatureFlag }) => {
      if (!intercomFeatureFlag) return;

      // intercomJwtService service will check if current user already has valid intercom token
      // if not we will hard shut down intercom to remove all cookies, settings etc.
      const intercomToken = await getIntercomTokenForCurrentUser(
        user,
        intercomHardShutdown,
      );
      if (!intercomToken) return;

      // Only boot with user jwt first. User companies and role etc. will be updated later
      intercomBoot({
        customAttributes: {
          app_id: process.env.INTERCOM_APP_ID,
          intercom_user_jwt: intercomToken,
        },
      });
      setIntercomInitiated(true);
    },
    [user, intercomHardShutdown, intercomBoot],
  );

  /**
   * Initializes the LaunchDarkly client with the current user's information
   *
   * This function identifies the user to LaunchDarkly with their personal details
   * and custom attributes. After successful identification, it initializes Intercom
   * with the returned feature flags.
   *
   * @returns {void}
   */
  const initLdClient = useCallback(() => {
    if (!ldClient || !user) return;

    const orgResourceId = activeOrganization?.orgResourceId || null;
    ldClient.identify(
      {
        key: user.uuid,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        custom: {
          ui: UI_TYPES.LIGHT_UI,
          intercom: true,
          orgResourceId,
        },
        privateAttributeNames: ['email'],
      },
      undefined,
      async (err, flags) => {
        // Per-org flags now hold this org's values. Recorded on failure too,
        // so pages gated on it fall back to the flags they have instead of
        // waiting forever (TS-9866).
        dispatch(ldContextIdentified({ key: user.uuid, orgResourceId }));
        if (flags) {
          await initIntercom(flags);
        }
      },
    );
  }, [user, initIntercom, activeOrganization?.orgResourceId, ldClient]);

  const getUnassignedTripsCount = useCallback(() => {
    if (!activeOrganization?.orgResourceId) return;

    dispatch(
      getTripsCount({
        query: {
          status: TRIP_STATUS_CODES.READY_TO_ASSIGN,
          requestedTripType: TRIP_TYPE_CODES.SCHEDULED,
          manualAssignmentVisible: true,
        },
        orgResourceId: activeOrganization.orgResourceId,
        sidebarMenuCountKey: 'unassignedTrips',
      }),
    );
  }, [activeOrganization]);

  const getNeedsActionTripsCount = useCallback(() => {
    if (!activeOrganization?.orgResourceId) return;

    dispatch(
      getNeedsActionTripsCountAction(
        activeOrganization?.orgResourceId,
        isCorrectionTableEnabled,
      ),
    );
  }, [activeOrganization, isCorrectionTableEnabled]);

  useEffect(() => {
    dispatch(loadOrganizations());

    const handleInvalidToken = (e) => {
      if (e.key === ACCESS_TOKEN_KEY && e.oldValue && !e.newValue) {
        clearIntercomJwt();
        if (window?.Intercom) {
          intercomHardShutdown();
        }
        dispatch(logoutSuccess());
      }
    };

    window.addEventListener('storage', handleInvalidToken);

    return () => {
      window.removeEventListener('storage', handleInvalidToken);
    };
  }, []);

  useEffect(() => {
    if (activeOrganization?.orgResourceId) dispatch(loadRoles());

    // Clear search results when active organization changes
    dispatch(resetGlobalSearchResults()); // Global search results
  }, [activeOrganization]);

  // Pick up mid-session role/permission changes without a full reload (TS-9877).
  useRefreshRolesOnNavigation(activeOrganization?.orgResourceId);

  const getRequestsTripsCount = useCallback(() => {
    if (!activeOrganization?.orgResourceId || !isExternalTripRequestsEnabled)
      return;

    dispatch(
      getTripsCount({
        query: {
          isRequest: true,
          status: TRIP_STATUS_CODES.APPROVAL_PENDING,
          requestedTripTypes: [
            TRIP_TYPE_CODES.SCHEDULED,
            TRIP_TYPE_CODES.MILEAGE_REIMBURSEMENT,
          ],
        },
        orgResourceId: activeOrganization.orgResourceId,
        sidebarMenuCountKey: 'requestsTrips',
        groupBy: 'confirmationNumber',
      }),
    );
  }, [activeOrganization, isExternalTripRequestsEnabled]);

  // Re-identify LD client when the user or organization actually changes to
  // refresh relevant feature flags (LaunchDarklyProvider inits with
  // orgResourceId: null, so this identify is what sets the org context).
  // Keyed on user.uuid rather than the user object: unrelated store updates
  // produce a new user reference, and each spurious identify() refetches all
  // flags and briefly empties useFlags().
  useEffect(() => {
    if (user?.uuid && activeOrganization?.orgResourceId) {
      initLdClient();
    }
  }, [user?.uuid, activeOrganization?.orgResourceId]);

  // have to introduce this useEffect as company and role data may not be available during intercom initiation
  useEffect(() => {
    if (!organizations.length || !currentUserRole?.role) return;
    const companies = organizations.map((org) => ({
      companyId: org.uuid,
      name: org.name,
    }));
    if (intercomInitiated && window?.Intercom) {
      intercomUpdate({
        companies,
        customAttributes: {
          environment: process.env.NODE_ENV,
          userRole: currentUserRole?.role,
        },
      });
    }
  }, [currentUserRole, organizations, intercomInitiated, user, intercomUpdate]);

  // Badge fetch deferred to NotificationPage visit (Option B) - no fetch on org/user change.

  // Depend on the primitive isExternalTripRequestsEnabled, not the ldFlags
  // object — ldFlags gets a fresh reference on every LaunchDarkly push and
  // would refetch all counts each time.
  useEffect(() => {
    initPendo(user, activeOrganization);

    if (activeOrganization) {
      Settings.defaultZone = activeOrganization?.timezone; // Set default timezone for Luxon
      dispatch(getTripGroupings(activeOrganization.orgResourceId));
      getRequestsTripsCount();
      getUnassignedTripsCount();
      getNeedsActionTripsCount();
      dispatch(getTripGroupings(activeOrganization.orgResourceId));
    }
  }, [activeOrganization, user, isExternalTripRequestsEnabled]);

  // Poll trip counts on a fixed interval
  useEffect(() => {
    if (!activeOrganization?.orgResourceId) return undefined;

    const fetchAllTripCounts = () => {
      getRequestsTripsCount();
      getUnassignedTripsCount();
      getNeedsActionTripsCount();
    };

    const intervalId = setInterval(
      fetchAllTripCounts,
      TRIP_COUNTS_POLL_INTERVAL_MS,
    );

    return () => clearInterval(intervalId);
  }, [
    activeOrganization,
    getRequestsTripsCount,
    getUnassignedTripsCount,
    getNeedsActionTripsCount,
  ]);

  // The app-wide trips CDC subscription MOVED from here to TripMapPage
  // (Active Trips) — TS-9404 follow-up.
  //
  // It called `subscribe_collection` for the whole organization, so every trip
  // change anywhere in the org streamed a full trip document to every logged-in
  // browser, on every page, for the entire session — App itself never consumed
  // the event, it only re-dispatched it for page-level sagas. On busy orgs that
  // is a continuous firehose of parse-and-discard allocation, which is what
  // exhausted the renderer (PartitionAlloc OOM, "Aw, Snap"). It also drove a
  // refetch loop in handleTripChangeWatchEventSaga and unbounded array growth
  // in handleSelectedTripUpdateEventSaga.
  //
  // Live vehicle tracking on Active Trips still works — that page now owns the
  // subscription and tears it down on unmount. Everywhere else, trip data
  // refreshes on navigation. Surfaces needing live updates already use their own
  // entity-scoped `subscribe` (TripDetailsPage, MemberProfileRoutes,
  // ProviderDetailsPage) — unaffected. Sidebar counts poll on
  // TRIP_COUNTS_POLL_INTERVAL_MS — unaffected.
  //
  // Do not reinstate this as a collection-wide subscription. If live org-wide
  // updates are needed, they must be server-side filtered and throttled.

  const isLoading =
    loading || isLoadingUser || isLoadingOrganizations || isLoadingAccessToken;

  const componentProps = {
    logout: () => {
      clearIntercomJwt();
      if (window?.Intercom) {
        intercomHardShutdown();
      }
      dispatch(attemptLogout());
    },
    inactivityModalVisibility: (visibility) =>
      dispatch(setInactivityModalVisibility(visibility)),
    user,
    activeOrganization,
    isLoading,
    activeTab,
    activeMenu,
    expanded,
    organizations,
    totalUnreadMessageCount,
    changeTab: (tab) => dispatch(setActiveTab(tab)),
    changeMenu: (menu) => dispatch(setActiveMenu(menu)),
    setExpanded: (isExpanded) => dispatch(setExpanded(isExpanded)),
    redirectTo: (path) => dispatch(push(`/${path}`)),
    toggleTheme: (theme) => dispatch(changeTheme(theme)),
    loadOrganizations: () => dispatch(loadOrganizations()),
    setActiveOrganization: (orgResourceId) =>
      dispatch(setActiveOrganization(orgResourceId)),
  };

  // We consider a user fully authenticated if
  // we have an access token AND
  // the user's account in state does NOT have an expired password
  const isAuthenticated = !!accessTokenService.getAccessToken();

  return (
    <ThemeProviderWrapper>
      <MuiThemeProvider theme={newLayoutTheme}>
        <KinetikThemeProvider theme={kinetikLightTheme}>
          <StyledComponentsThemeProvider theme={newLayout}>
            <TableProvider>
              {/*
               * App level error boundary (TS-9461/TS-9473). Sits inside the
               * theme providers so the fallback can use the design system, and
               * inside TableProvider/above GlobalStyle so a caught error does
               * not tear down global styles. Everything a user interacts with —
               * the navbar (global member search) and every routed page — is
               * rendered below this point, so a render throw here degrades to a
               * recoverable fallback instead of blanking the page.
               */}
              <AppErrorBoundary>
                <Alerts
                  messages={messages}
                  redirectUrl={redirectUrl}
                  redirectPageName={redirectPageName}
                  removeOldestMessage={() => dispatch(removeOldestMessage())}
                  removeMessage={(id) => dispatch(removeOneMessage(id))}
                  appNotification={
                    appNotifications.length &&
                    appNotifications[appNotifications.length - 1]
                  }
                  removeAppNotification={() =>
                    dispatch(removeAppNotification())
                  }
                />
                <ActionAlerts
                  messages={alertMessages}
                  removeMessage={(uuid) => dispatch(removeActionAlert(uuid))}
                />
                <Suspense fallback={null}>
                  <ToastNotifications />
                </Suspense>
                <InactivityModal
                  inactivityModalIsVisible={inactivityModalVisibility}
                  logout={() => {
                    dispatch(setInactivityModalVisibility(false));
                    // to reset intercom cookies
                    if (window?.Intercom) {
                      intercomHardShutdown();
                    }
                    dispatch(attemptLogout('Logout due to inactivity'));
                  }}
                />
                {isLoading ? (
                  <LoadingSpinner height="100%" position="absolute" />
                ) : (
                  <AppRoutes
                    componentProps={componentProps}
                    isLoading={isLoading}
                    isAuthenticated={isAuthenticated}
                  />
                )}
              </AppErrorBoundary>
              <GlobalStyle />
            </TableProvider>
          </StyledComponentsThemeProvider>
        </KinetikThemeProvider>
      </MuiThemeProvider>
    </ThemeProviderWrapper>
  );
};

export default memo(App);
